import { useEffect } from 'react';
import { useActivePractice, useDispatch } from '@witmetrics/data-store';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import Overlay from '@/components/Overlay';
import LoopingEllipsis from '@/components/LoopingEllipsis';
import TabsColumn from './TabsColumn';
import CaseHeader from './CaseHeader';
import TimelineSection from './TimelineSection';
import { classes, fetchProjectData, fetchProjectFiles } from './utils';

export default function CaseOverlay() {
  const dispatch = useDispatch();
  const { onApiError, activeProjectId, onClearActiveProject } = useAppState();
  const activePractice = useActivePractice();
  const [isFetchingData, toggleFetchingData] = useToggle(true);
  const [isFetchingFiles, toggleFetchingFiles] = useToggle(true);

  useEffect(() => {
    if (activeProjectId && activePractice) {
      fetchData();
      fetchFileData();
    }
  }, [activeProjectId, activePractice]);

  const fetchData = async () => {
    try {
      toggleFetchingData(true);
      const results = await fetchProjectData(
        activeProjectId!,
        activePractice!.id
      );
      dispatch.addProjects([results.unisonProject]);
      dispatch.addProjectTasks(results.tasks);
      dispatch.addProjectNotes(results.notes);
      dispatch.addProjectKeypoints(results.keypoints);
      dispatch.addProjectOrders(results.orders);
      dispatch.addProjectSubmissions(results.unisonProjectFormSubmissions);
      dispatch.addForms(results.forms);
      dispatch.addFormVersions(results.formVersions);
      dispatch.addSubmissions(results.formSubmissions);
      dispatch.addProjectScanServices(results.services);
      dispatch.addPracticeUsers(results.practiceUsers);
      dispatch.addKeypointTypes(results.keypointTypes);
      toggleFetchingData(false);
    } catch (err) {
      onApiError(err, 'Error fetching case data', () => fetchData());
    }
  };

  const fetchFileData = async () => {
    try {
      toggleFetchingFiles(true);
      const results = await fetchProjectFiles(activeProjectId!);
      dispatch.addProjectFileCollections(results.projectFileCollections);
      dispatch.addFileCollectionFiles(results.fileCollectionFiles);
      dispatch.addFileNotes(results.fileNotes);
      dispatch.addFileCollections(results.fileCollections);
      toggleFetchingFiles(false);
    } catch (err) {
      onApiError(err, 'Error fetching files', () => fetchFileData());
    }
  };

  if (!activeProjectId) return null;

  return (
    <Overlay
      className={classes.overlay}
      variant="glass"
      onClose={onClearActiveProject}>
      <CaseHeader />
      <div className={classes.content}>
        <div className={classes.leftColumn}>
          {isFetchingData ? (
            <div className={classes.loading}>
              Fetching case
              <LoopingEllipsis />
            </div>
          ) : (
            <TimelineSection />
          )}
        </div>
        <TabsColumn
          isFetchingData={isFetchingData}
          isFetchingFiles={isFetchingFiles}
        />
      </div>
    </Overlay>
  );
}
